import React, { useContext, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { secured } from "react-abac";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Button, Dropdown, Grid, Icon, InputSelect, InputText, Link, Modal, Picture, Section, Spinner, Text } from "~/components-shared";
import CoreContext from "~/lib/context";
import communitySelectors from "~/store/communities/selectors";
import communitiesActionCreators from "~/store/communities/actions";
import { useLocation, useParams } from "react-router-dom";
import memberSelectors from "~/store/members/selectors";
import membersActionCreators from "~/store/members/actions";
import moment from "moment";
import DefaultSideBar from "~/components/DefaultSideBar";
import MemberCard from "~/components/MemberCard";
import authSelectors from "~/store/auth/selectors";
import CommunityUpdateModal from "~/components/CommunityUpdateModal";
import CommunityDeleteModal from "~/components/CommunityDeleteModal";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import lists from "~/lib/lists";
import ErrorSection from "~/components/ErrorSection";
import useAbacExt from "~/hooks/abac";
import integrationSelectors from "~/store/integrations/selectors";
import CommunityInviteMembersModal from "~/components/CommunityInviteMembersModal";
import qs from "qs";
import MemberInviteCommunityModal from "~/components/MemberInviteCommunityModal";
import { getSlackSpaceUrl } from "~/lib/helpers";
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  margin-left: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 3;
}}px;
`;
const Community = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    communityId
  } = useParams();
  const user = useSelector(authSelectors.getUser);
  const status = useSelector(communitySelectors.getStatusByCommunity(communityId));
  const action = useSelector(communitySelectors.getAction);
  const memberAction = useSelector(memberSelectors.getAction);
  const error = useSelector(communitySelectors.getErrorByCommunity(communityId));
  const community = useSelector(communitySelectors.getCommunity(communityId));
  const memberStatus = useSelector(memberSelectors.getStatusByCommunity(communityId));
  const memberSort = useSelector(memberSelectors.getSortByCommunity);
  const memberFilter = useSelector(memberSelectors.getFilterByCommunity);
  const members = useSelector(memberSelectors.getMembersByCommunity(communityId));
  const roles = useSelector(memberSelectors.getRoles);
  const rolesStatus = useSelector(memberSelectors.getRolesStatus);
  const {
    hasAfter
  } = useSelector(memberSelectors.getPaginationByCommunity);
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const {
    search
  } = useLocation();
  const {
    acceptJoinInvitation,
    leadResponse,
    leadRequestId
  } = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  useEffect(() => {
    setSidebar(/*#__PURE__*/React.createElement(DefaultSideBar, null));
  }, [setSidebar]);
  useEffect(() => {
    if (rolesStatus === StatusStateEnum.IDLE) {
      dispatch(membersActionCreators.fetchRolesStarted());
    }
  }, [rolesStatus, dispatch]);
  useEffect(() => {
    if (acceptJoinInvitation === "true") {
      dispatch(communitiesActionCreators.joinStarted(communityId));
    }
  }, [acceptJoinInvitation]);
  useEffect(() => {
    if (leadRequestId && leadResponse === "approve") {
      dispatch(communitiesActionCreators.leaderApproveStarted(communityId, leadRequestId));
    } else if (leadRequestId && leadResponse === "decline") {
      dispatch(communitiesActionCreators.leaderDeclineStarted(communityId, leadRequestId));
    }
  }, [leadResponse, leadRequestId]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE && !community) {
      dispatch(communitiesActionCreators.fetchOneStarted(communityId));
    }
  }, [communityId, dispatch, status]);
  useEffect(() => {
    if (memberStatus === StatusStateEnum.IDLE) {
      dispatch(membersActionCreators.fetchByCommunityStarted(communityId));
    }
  }, [memberStatus, dispatch, communityId]);
  if (!community && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!community) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const handleEdit = () => {
    dispatch(communitiesActionCreators.actionChanged(CRUDActionEnum.UPDATE, communityId));
  };
  const handleDelete = () => {
    dispatch(communitiesActionCreators.actionChanged(CRUDActionEnum.DELETE, communityId));
  };
  const handleJoinCommunity = () => {
    dispatch(communitiesActionCreators.joinStarted(communityId));
  };
  const handleLeaveCommunity = () => {
    dispatch(communitiesActionCreators.leaveStarted(communityId));
  };
  const handleSort = newSort => {
    dispatch(membersActionCreators.communitySorted(communityId, newSort));
    dispatch(membersActionCreators.communityLeadersSorted(communityId, newSort));
  };
  const handleFilter = newFilter => {
    dispatch(membersActionCreators.communityFiltered(communityId, newFilter));
    dispatch(membersActionCreators.communityLeadersFiltered(communityId, newFilter));
  };
  const handleLoadMore = () => {
    dispatch(membersActionCreators.communityLoadedMore(communityId));
  };
  const handleInviteToCommunity = () => {
    dispatch(communitiesActionCreators.actionChanged(CRUDActionEnum.INVITE, communityId));
  };
  return /*#__PURE__*/React.createElement(Section, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: members.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      s: {
        mt: 6
      },
      center: true
    })
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 10,
    s: {
      mb: 10
    },
    template: "150px 1fr",
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Picture, {
    src: community.picture,
    name: community.name,
    color: community.color || theme.colors.secondary.main,
    width: "150px",
    height: "150px"
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h1"
  }, community.name), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    align: "center",
    s: {
      ml: 3
    }
  }, userHas(AuthoritiesEnum.COMMUNITIES_WRITE) && community.memberIds.includes(user?.id || "") ? /*#__PURE__*/React.createElement(Button, {
    rounded: true,
    s: {
      px: 5,
      py: 2
    },
    color: "grey",
    display: "inline-block",
    onClick: handleLeaveCommunity
  }, t("common.actions.leave")) : /*#__PURE__*/React.createElement(Button, {
    rounded: true,
    s: {
      px: 5,
      py: 2
    },
    color: "textSecondary",
    display: "inline-block",
    onClick: handleJoinCommunity
  }, "+ ", t("common.actions.join")), userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE], community.createdBy.id) && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE], community.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleEdit();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE], community.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary",
    display: "inline-block",
    s: {
      mb: 2
    }
  }, t("Community.founded"), " ", moment(community.founded).format("MMM YYYY")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary"
  }, community.mission), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    justify: "start",
    s: {
      mt: 3
    }
  }, slackIntegration && community.slackId && /*#__PURE__*/React.createElement(Link, {
    href: `${getSlackSpaceUrl(slackIntegration)}/archives/${community.slackId}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "message"
  }), " #", community.slackName), community.email && /*#__PURE__*/React.createElement(Link, {
    href: `mailto:${community.email}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "mail"
  }), " ", community.email), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    s: {
      px: 2
    },
    color: theme.colors.text.secondary
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    scale: 3
  }), " ", community.memberIds.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.secondary.main
  }, "(", community.activeMemberIds.length, ")"))))), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 5,
    s: {
      mb: 8
    },
    template: userHas(AuthoritiesEnum.INVITATIONS_WRITE) ? "1fr 170px 170px 3.3rem" : "1fr 170px 170px"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: memberFilter?.query || "",
    placeholder: t("Community.searchMember"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "transparent",
    onChange: value => handleFilter({
      ...memberFilter,
      query: value
    }),
    rounded: true,
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: memberFilter?.roleIds || "",
    onChange: value => handleFilter({
      ...memberFilter,
      roleIds: [value]
    }),
    options: [{
      id: "",
      value: t("common.userRoles.all")
    }, ...roles.map(_ref3 => {
      let {
        id,
        name
      } = _ref3;
      return {
        id,
        value: name
      };
    })],
    rounded: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: memberSort,
    onChange: value => handleSort(value),
    options: lists.members.sort.names,
    rounded: true
  }), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Community.inviteMembersToCommunity"),
    s: {
      px: 3,
      py: 3
    },
    onClick: handleInviteToCommunity,
    rounded: true,
    color: "transparent",
    shadow: true
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "user-plus",
    scale: 5
  }))), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 5
    }
  }, t("Community.communityLeaders")), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 5
    }
  }, t("Community.communityMembers")), !members.length && memberStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : members.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 8,
    align: "stretch",
    equal: true
  }, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, members.map(member => /*#__PURE__*/React.createElement(MemberCard, {
    key: member.id,
    member: member,
    shadow: true,
    border: false
  })))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block"
  }, t("Community.noMembersFound"))), userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(CommunityUpdateModal, null)), userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(CommunityDeleteModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.INVITE
  }, /*#__PURE__*/React.createElement(CommunityInviteMembersModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: memberAction === CRUDActionEnum.INVITE
  }, /*#__PURE__*/React.createElement(MemberInviteCommunityModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.COMMUNITIES_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(Community), () => true);